<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { startOfMonth, endOfDay } from 'date-fns';
import { useMetaCampaignsCampaignsStore } from 'dashboard/stores/metaCampaignsCampaigns';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { useMetaCampaignsFormatters } from '../composables/useMetaCampaignsFormatters';
import CampaignAdSetsTab from '../components/CampaignAdSetsTab.vue';
import CampaignAdsTab from '../components/CampaignAdsTab.vue';
import CampaignLeadsTab from '../components/CampaignLeadsTab.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const campaignsStore = useMetaCampaignsCampaignsStore();
const { spendChartCollection } = useMetaCampaignsFormatters();

const campaign = computed(() => campaignsStore.getSelectedCampaign);
const isLoading = computed(() => campaignsStore.uiFlags.fetchingItem);

// The dashboard's campaign table links here with the period it was already
// showing (?since=&until=), so clicking a campaign never silently switches
// periods. Falling back to month-to-date only matters for a direct/bookmarked
// visit with no query string.
const parseUnixParam = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0
    ? new Date(parsed * 1000)
    : fallback;
};

const now = new Date();
const hasQueryPeriod = Boolean(route.query.since && route.query.until);
const dateRange = ref([
  parseUnixParam(route.query.since, startOfMonth(now)),
  parseUnixParam(route.query.until, endOfDay(now)),
]);
const dateRangeType = ref(
  hasQueryPeriod
    ? DATE_RANGE_TYPES.CUSTOM_RANGE
    : DATE_RANGE_TYPES.MONTH_TO_DATE
);

const since = computed(() => Math.floor(dateRange.value[0].getTime() / 1000));
const until = computed(() => Math.floor(dateRange.value[1].getTime() / 1000));

const loadCampaign = () => {
  campaignsStore.fetchOne(route.params.campaignId, {
    since: since.value,
    until: until.value,
  });
};

// Keeps the period deep-linkable/refreshable/open-in-new-tab-able, the same
// way the leads table already opens conversations in a new tab.
const syncQuery = () => {
  router.replace({
    query: { ...route.query, since: since.value, until: until.value },
  });
};

const onDateRangeChanged = ([start, end, rangeType]) => {
  dateRange.value = [start, end];
  dateRangeType.value = rangeType;
  syncQuery();
  loadCampaign();
};

const chartCollection = computed(() =>
  spendChartCollection(
    campaign.value?.daily_metrics || [],
    t('META_CAMPAIGNS.DASHBOARD.CHART.SPEND')
  )
);

const tabs = computed(() => [
  {
    name: 'ad_sets',
    label: t('META_CAMPAIGNS.CAMPAIGN_DETAIL.TABS.AD_SETS'),
  },
  { name: 'ads', label: t('META_CAMPAIGNS.CAMPAIGN_DETAIL.TABS.ADS') },
  { name: 'leads', label: t('META_CAMPAIGNS.CAMPAIGN_DETAIL.TABS.LEADS') },
]);
const activeTabIndex = ref(0);
// Once visited a tab stays mounted (toggled with v-show below) so switching
// back doesn't refetch; only the active tab is fetched up front.
const visitedTabs = ref(new Set(['ad_sets']));

const handleTabChanged = tab => {
  const index = tabs.value.findIndex(candidate => candidate.name === tab.name);
  if (index === -1) return;
  activeTabIndex.value = index;
  visitedTabs.value.add(tab.name);
};

const isTabActive = name => tabs.value[activeTabIndex.value]?.name === name;

onMounted(() => {
  syncQuery();
  loadCampaign();
});

// Vue Router reuses this component instance when only the route param
// changes (e.g. browser back/forward between two campaigns visited in the
// same tab) — without this watcher, onMounted alone would leave the old
// campaign's data on screen under the new campaign's header.
watch(
  () => route.params.campaignId,
  () => {
    activeTabIndex.value = 0;
    visitedTabs.value = new Set(['ad_sets']);
    loadCampaign();
  }
);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="sticky top-0 z-10 px-6 bg-n-surface-1 border-b border-n-weak"
    >
      <div
        class="w-full max-w-5xl mx-auto flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between md:h-16 md:py-0"
      >
        <div class="flex items-center gap-3 min-w-0">
          <Button
            size="sm"
            variant="ghost"
            icon="i-lucide-arrow-left"
            @click="router.back()"
          />
          <h1 class="text-xl font-medium text-n-slate-12 truncate">
            {{ campaign?.name || t('META_CAMPAIGNS.CAMPAIGN_DETAIL.TITLE') }}
          </h1>
        </div>
        <WootDatePicker
          v-model:date-range="dateRange"
          v-model:range-type="dateRangeType"
          @date-range-changed="onDateRangeChanged"
        />
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-6 flex flex-col gap-6">
        <div v-if="isLoading" class="flex items-center justify-center py-20">
          <span
            class="i-lucide-loader-circle animate-spin text-3xl text-n-slate-9"
          />
        </div>

        <template v-else-if="campaign">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5">
              <p class="text-xs text-n-slate-10">
                {{ t('META_CAMPAIGNS.CAMPAIGN_DETAIL.OBJECTIVE') }}
              </p>
              <p class="text-sm font-medium text-n-slate-12 mt-1">
                {{ campaign.objective }}
              </p>
            </div>
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5">
              <p class="text-xs text-n-slate-10">
                {{ t('META_CAMPAIGNS.CAMPAIGN_DETAIL.STATUS') }}
              </p>
              <p class="text-sm font-medium text-n-slate-12 mt-1">
                {{ campaign.status }}
              </p>
            </div>
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5">
              <p class="text-xs text-n-slate-10">
                {{ t('META_CAMPAIGNS.CAMPAIGN_DETAIL.AD_ACCOUNT') }}
              </p>
              <p class="text-sm font-medium text-n-slate-12 mt-1">
                {{ campaign.ad_account_name }}
              </p>
            </div>
          </div>

          <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5">
            <h2 class="text-sm font-semibold text-n-slate-12 mb-4">
              {{ t('META_CAMPAIGNS.DASHBOARD.CHART.TITLE') }}
            </h2>
            <div class="h-64">
              <LineChart
                v-if="campaign.daily_metrics?.length"
                :collection="chartCollection"
              />
              <p v-else class="text-sm text-n-slate-9">
                {{ t('META_CAMPAIGNS.DASHBOARD.CHART.NO_DATA') }}
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <TabBar
              :tabs="tabs"
              :initial-active-tab="activeTabIndex"
              @tab-changed="handleTabChanged"
            />

            <CampaignAdSetsTab
              v-if="visitedTabs.has('ad_sets')"
              v-show="isTabActive('ad_sets')"
              :campaign-id="campaign.id"
              :since="since"
              :until="until"
            />
            <CampaignAdsTab
              v-if="visitedTabs.has('ads')"
              v-show="isTabActive('ads')"
              :campaign-id="campaign.id"
              :since="since"
              :until="until"
            />
            <CampaignLeadsTab
              v-if="visitedTabs.has('leads')"
              v-show="isTabActive('leads')"
              :campaign="campaign"
            />
          </div>
        </template>
      </div>
    </main>
  </section>
</template>
